
import React, { useState, useEffect } from 'react';
import { SectionWrapper } from './SectionWrapper';
import { ARTICLES } from '../constants';
import { X, Feather, ChevronRight } from 'lucide-react';
import { Article } from '../types';

export const Articles: React.FC = () => {
  const [readingArticle, setReadingArticle] = useState<Article | null>(null);
  const [typedContent, setTypedContent] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isSealed, setIsSealed] = useState(false);

  useEffect(() => {
    if (readingArticle && readingArticle.content) {
      document.body.style.overflow = 'hidden';
      setTypedContent('');
      setIsTyping(true);
      setIsSealed(false);
      let i = 0;
      const fullText = readingArticle.content;
      const timer = setInterval(() => {
        if (i < fullText.length) {
          setTypedContent((prev) => prev + fullText.charAt(i));
          i++;
        } else {
          setIsTyping(false);
          clearInterval(timer);
        }
      }, 25);
      return () => clearInterval(timer);
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [readingArticle]);

  const handleClose = () => {
    setReadingArticle(null);
  };

  const handleSeal = () => {
    setIsSealed(true);
  };

  return (
    <>
      <SectionWrapper>
        <header className="text-center mb-28 animate-fade-in flex flex-col items-center">
          {/* 调整1：标题字重从 black 降为 bold，颜色从 900 降为 800，增加优雅感 */}
          <h1 className="font-serif text-3xl md:text-4xl text-stone-800 tracking-widest mb-4 font-bold">
            咨询手记
          </h1>
          <div className="flex flex-col items-center gap-2">
            <span className="text-stone-400 font-sans uppercase text-xs tracking-[0.6em] font-bold">The Reflections</span>
            <p className="text-stone-600 font-serif text-lg md:text-xl mt-10 max-w-xl leading-loose italic">
              “在理论的罅隙里，记录真实的回响。”
            </p>
          </div>
        </header>

        <section className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
          {ARTICLES.map((article) => (
            <article 
              key={article.id} 
              onClick={() => setReadingArticle(article)}
              className="group bg-white p-10 border border-stone-200 shadow-sm hover:shadow-xl transition-all duration-700 cursor-pointer flex flex-col justify-between min-h-[300px] rounded-sm"
            >
              <div>
                <header className="flex items-center justify-between mb-8">
                  <span className="text-[10px] font-mono text-stone-400 border border-stone-200 px-3 py-1 rounded-full uppercase tracking-widest font-medium">
                    {article.readTime}
                  </span>
                  <div className="w-1.5 h-1.5 rounded-full bg-rose-200 group-hover:bg-rose-400 transition-all"></div>
                </header>
                {/* 调整2：卡片标题字号适中，字重标准 bold，颜色深灰 */}
                <h2 className="font-serif text-xl md:text-2xl text-stone-800 font-bold mb-6 leading-snug group-hover:text-rose-900 transition-colors">
                  {article.title}
                </h2>
                {/* 
                   调整3：摘要
                   - text-[15px] md:text-base -> text-base md:text-lg (加大 10% 左右)
                */}
                <p className="text-stone-700 font-serif leading-relaxed text-base md:text-lg text-justify line-clamp-4 font-normal">
                  {article.summary}
                </p>
              </div>
              <footer className="mt-8 pt-6 border-t border-stone-100 flex justify-end">
                 <span className="text-xs text-stone-400 group-hover:text-rose-500 transition-all flex items-center gap-1 font-serif tracking-widest">
                   阅读更多 <ChevronRight size={14} />
                 </span>
              </footer>
            </article>
          ))}
        </section>
      </SectionWrapper>

      {readingArticle && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 md:p-10 bg-stone-900/40 backdrop-blur-md animate-fade-in" onClick={handleClose}>
           <article className="bg-[#fffefc] w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-sm shadow-2xl relative p-8 md:p-16" onClick={e => e.stopPropagation()}>
              <button onClick={handleClose} className="absolute top-6 right-6 text-stone-400 hover:text-stone-900 transition-colors z-20 bg-stone-100 p-2 rounded-full"><X size={24} /></button>
              
              <header className="mb-12 border-b border-stone-100 pb-10">
                 <h1 className="font-serif text-2xl md:text-3xl text-stone-800 font-bold mb-4">{readingArticle.title}</h1>
                 <p className="text-stone-400 text-[10px] font-mono tracking-widest uppercase">{readingArticle.readTime} / Yanyu Reflection</p>
              </header>

              <div className="prose prose-stone max-w-none min-h-[40vh]">
                 <p className="font-serif text-stone-700 text-lg md:text-xl leading-[2.2] whitespace-pre-wrap text-justify font-normal">
                    {typedContent}
                    {isTyping && <span className="inline-block w-1.5 h-5 bg-rose-300 ml-1 animate-pulse"></span>}
                 </p>
              </div>

              {!isTyping && (
                <footer className="mt-20 pt-20 border-t border-dashed border-stone-200 flex flex-col items-center animate-fade-in">
                   {!isSealed ? (
                     <div className="text-center space-y-8">
                        <p className="text-stone-500 font-serif italic text-lg tracking-wider">若文字在内心引起了回响...</p>
                        <button 
                          onClick={handleSeal}
                          className="group relative flex items-center gap-3 px-10 py-3 bg-white border border-stone-200 rounded-full text-stone-600 font-serif tracking-widest hover:border-rose-300 hover:text-rose-500 hover:shadow-lg transition-all duration-500"
                        >
                           <Feather className="text-rose-300 group-hover:rotate-12 transition-transform" size={18} />
                           <span className="font-bold text-sm">封存这份感受</span>
                        </button>
                     </div>
                   ) : (
                     <div className="flex flex-col items-center space-y-10 animate-fade-in">
                        <div className="group relative flex flex-col items-center">
                           <button onClick={handleClose} className="relative animate-[stamp_0.5s_cubic-bezier(0.175,0.885,0.32,1.275)_forwards] cursor-pointer">
                              <div className="w-24 h-24 relative flex items-center justify-center transform rotate-[-5deg] group-hover:scale-105 group-hover:-rotate-3 transition-transform duration-500">
                                   <div className="absolute inset-0 rounded-[45%_55%_48%_52%/50%_48%_52%_50%] bg-[#9f1239] shadow-[inset_-4px_-4px_10px_rgba(50,0,0,0.5),inset_4px_4px_10px_rgba(255,255,255,0.3),0_10px_20px_rgba(159,18,57,0.4)]"></div>
                                   <div className="absolute inset-3 rounded-full border-[1.5px] border-[#be123c]/40 shadow-[inset_1px_1px_3px_rgba(0,0,0,0.4)]"></div>
                                   <span className="font-serif text-3xl text-[#7f1d1d] font-bold drop-shadow-[1px_1px_0px_rgba(255,255,255,0.2)] shadow-inner relative top-[-1px]">屿</span>
                                   <div className="absolute top-4 left-4 w-5 h-2.5 bg-white/20 rounded-full blur-[2px] transform -rotate-45"></div>
                              </div>
                              <div className="absolute inset-0 bg-rose-500/10 blur-xl animate-ping rounded-full pointer-events-none"></div>
                           </button>
                           <p className="text-[10px] font-mono text-stone-400 tracking-[0.3em] uppercase mt-4 opacity-0 animate-fade-in delay-1000 group-hover:opacity-100 transition-opacity">Click seal to return</p>
                        </div>
                        <p className="font-serif text-rose-800/80 text-lg font-bold italic tracking-[0.2em] animate-fade-in delay-500">愿这份力量伴随你</p>
                     </div>
                   )}
                </footer>
              )}
           </article>
        </div>
      )}

      <style>{`
        @keyframes stamp { 0% { transform: scale(3); opacity: 0; filter: blur(10px); } 100% { transform: scale(1); opacity: 1; filter: blur(0px); } }
      `}</style>
    </>
  );
};
